import { DocumentTypeCodeEnum } from '@elearning-platform/api-types/enums';
import {
  ConfirmContactDataChangeInterface,
  DocumentArchiveAddFileResultInterface,
  DocumentArchiveListItemInterface,
  DocumentArchiveListQueryInterface,
  DocumentArchiveUploadResultInterface,
  IPaginatorData,
  ProfileExperienceDetailsInterface,
  ResendContactDataTokenInterface,
  SetProfileDetailInterface,
  UserProfileModelInterface,
} from '@elearning-platform/api-types/interfaces';
import { UpdateContactDataType } from '@elearning-platform/api-types/types';
import { stringify } from 'query-string';

import { api } from '../api';

export const updateContactData: (data: UpdateContactDataType) => Promise<void> = (
  data: UpdateContactDataType
): Promise<void> => api.post(
  'profile/contact-data', data
);

export const resendContactDataConfirmationToken: (data: ResendContactDataTokenInterface) => Promise<void> = (
  data: ResendContactDataTokenInterface
): Promise<void> => api.post(
  'profile/contact-data/resend', data
);

export const confirmContactData: (data: ConfirmContactDataChangeInterface) => Promise<void> = (
  data: ConfirmContactDataChangeInterface
): Promise<void> => api.post(
  'profile/contact-data/confirm', data
);

export const getUserData: () => Promise<UserProfileModelInterface> = (): Promise<UserProfileModelInterface> => api.get('/profile');

export const getUserProfileExperience: () => Promise<ProfileExperienceDetailsInterface[]>
= (): Promise<ProfileExperienceDetailsInterface[]> => api.get('/profile/experiences');

export const getUserDocumentArchiveList: (
  params?: DocumentArchiveListQueryInterface
) => Promise<IPaginatorData<DocumentArchiveListItemInterface>>
= (
  params: DocumentArchiveListQueryInterface = {}
): Promise<IPaginatorData<DocumentArchiveListItemInterface>> => api.get(
  `/profile/archive/documents?${stringify(params, { skipEmptyString: true })}`
);

export const downloadDocumentFromMyArchive: (documentId: number) => Promise<void> = (documentId: number): Promise<void> => api.get(
  `/profile/archive/documents/${documentId}/download`, { responseType: 'blob' }
);

export const downloadFileFromMyArchive: (documentId: number, fileId: number) => Promise<void> = (
  documentId: number, fileId: number
): Promise<void> => api.get(
  `/profile/archive/documents/${documentId}/files/${fileId}/download`, { responseType: 'blob' }
);

export const downloadFileFromMyArchiveAsStream: (documentId: number, fileId: number) => Promise<string> = (
  documentId: number, fileId: number
): Promise<string> => api.get(
  `/profile/archive/documents/${documentId}/files/${fileId}/download`, { customEnv: { notEncryptedResponse: true } }
);

export const deleteUserDocumentArchiveFiles: (documentId: number) => Promise<void> = (
  documentId: number
): Promise<void> => api.delete(
  `/profile/archive/documents/${documentId}`
);

export const addUserDocumentArchive: (
  documentTypeCode: DocumentTypeCodeEnum, files: File[], expiresAt?: string
) => Promise<DocumentArchiveUploadResultInterface> = (
  documentTypeCode: DocumentTypeCodeEnum, files: File[], expiresAt?: string
): Promise<DocumentArchiveUploadResultInterface> => {
  const formData: FormData = new FormData();
  // todo: add expiresAt handler in next mr when archive button will be available
  if (expiresAt) {
    formData.append('expiresAt', expiresAt);
  } else {
    formData.append('expiresAt', '1824018787');
  }
  formData.append('documentTypeCode', documentTypeCode);
  files.forEach((file: File): void => formData.append('files', file));

  return api.post('/profile/archive/documents', formData, { customEnv: { notEncryptedRequest: true } });
};

export const addFilesToUserDocumentArchive: (
  documentId: number, files: File[]
) => Promise<DocumentArchiveAddFileResultInterface> = (
  documentId: number, files: File[]
): Promise<DocumentArchiveAddFileResultInterface> => {
  const formData: FormData = new FormData();
  files.forEach((file: File): void => formData.append('files', file));

  return api.post(`profile/archive/documents/${documentId}/files`, formData, { customEnv: { notEncryptedRequest: true } });
};

export const deleteFileFromUserDocumentArchive: (documentId: number, fileId: number) => Promise<void> = (
  documentId: number, fileId: number
): Promise<void> => api.delete(
  `profile/archive/documents/${documentId}/files/${fileId}`
);

export const updateUserProfileDetails: (data: SetProfileDetailInterface) => Promise<void> = (
  data: SetProfileDetailInterface
): Promise<void> => api.post('/profile/details', data);
